import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ServerTimeStatus = 'syncing' | 'synced' | 'offline';

interface ServerTimeContextValue {
  now: number | null;
  nowDate: Date | null;
  status: ServerTimeStatus;
  refresh: () => Promise<void>;
}

const ServerTimeContext = createContext<ServerTimeContextValue | undefined>(undefined);

const getPerformanceNow = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now();
  return 0;
};

export const ServerTimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [syncPoint, setSyncPoint] = useState<{ serverMs: number; performanceMs: number } | null>(null);
  const [now, setNow] = useState<number | null>(null);
  const [status, setStatus] = useState<ServerTimeStatus>('syncing');

  const refresh = useCallback(async () => {
    try {
      const requestStarted = getPerformanceNow();
      const response = await fetch('/api/time', { cache: 'no-store', credentials: 'same-origin' });
      const payload = await response.json().catch(() => null);
      const requestFinished = getPerformanceNow();
      const serverMs = Number(payload && payload.unixMs);
      if (!response.ok || !Number.isFinite(serverMs)) throw new Error('Invalid server time response');

      const adjustedServerMs = serverMs + ((requestFinished - requestStarted) / 2);
      setSyncPoint({ serverMs: adjustedServerMs, performanceMs: requestFinished });
      setNow(adjustedServerMs);
      setStatus('synced');
    } catch (error) {
      setStatus('offline');
    }
  }, []);

  useEffect(() => {
    refresh();
    const resyncInterval = window.setInterval(refresh, 60000);
    return () => window.clearInterval(resyncInterval);
  }, [refresh]);

  useEffect(() => {
    const tick = () => {
      setNow(syncPoint ? syncPoint.serverMs + (getPerformanceNow() - syncPoint.performanceMs) : null);
    };
    tick();
    const tickInterval = window.setInterval(tick, 1000);
    return () => window.clearInterval(tickInterval);
  }, [syncPoint]);

  const value = useMemo<ServerTimeContextValue>(() => ({
    now,
    nowDate: now === null ? null : new Date(now),
    status,
    refresh,
  }), [now, refresh, status]);

  return <ServerTimeContext.Provider value={value}>{children}</ServerTimeContext.Provider>;
};

export const useServerTime = () => {
  const context = useContext(ServerTimeContext);
  if (!context) throw new Error('useServerTime must be used within a ServerTimeProvider');
  return context;
};
